import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";

const FORM_URL = "https://readdy.ai/api/form/db7jeeatp9sllopf7gkg";

const values = [
  { icon: "ri-movie-2-line", title: "内容丰富", desc: "涵盖电影、电视剧及多个热门题材，片库持续扩充。" },
  { icon: "ri-sort-desc", title: "分类清晰", desc: "支持类型、地区、年份多维筛选，快速找到想看的内容。" },
  { icon: "ri-speed-up-line", title: "更新及时", desc: "热播剧集同步追更，第一时间掌握更新进度。" },
  { icon: "ri-device-line", title: "多端友好", desc: "移动端与桌面端自适应，随时浏览影视资讯。" },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() ? serverMsg : "提交失败，请稍后重试"
        );
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src="https://readdy.ai/api/search-image?query=Cinematic%20abstract%20movie%20theater%20atmosphere%20with%20rows%20of%20softly%20glowing%20seats%20and%20a%20large%20blank%20screen%2C%20deep%20navy%20and%20warm%20orange%20accent%20lighting%2C%20elegant%20premium%20mood%2C%20wide%20panoramic%20composition&width=1600&height=600&seq=tl-about-hero&orientation=landscape"
          alt="天龙影院品牌形象图"
          title="天龙影院 高清影视平台"
          className="w-full h-[280px] md:h-[360px] object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/75 to-primary-950/40"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="px-6 md:px-12">
            <Breadcrumb items={[{ label: "关于我们" }]} />
            <h1 className="mt-4 text-2xl md:text-4xl font-heading font-bold text-background-50">关于天龙影院</h1>
            <p className="mt-3 max-w-2xl text-sm md:text-base font-body leading-relaxed text-background-200">
              天龙影院是一个专注高清影视内容的在线浏览平台，致力于为热爱影视的用户提供内容丰富、分类清晰、检索便捷的观影浏览体验。
            </p>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 py-12 flex flex-col gap-14">
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">网站定位</h2>
            </div>
            <div className="flex flex-col gap-4 text-sm md:text-base font-body leading-loose text-foreground-700">
              <p>
                天龙影院面向广大影视爱好者，汇聚最新电影与热播电视剧资源，覆盖动作、爱情、科幻、悬疑、古装、都市等多元题材，让不同喜好的观众都能轻松找到想看的内容。
              </p>
              <p>
                我们注重内容的丰富度与浏览的便捷性：通过分类入口、多维筛选、搜索与个性化推荐，帮助用户在海量片库中快速定位目标影视，并清晰了解每一部作品的资料与剧集进度。
              </p>
              <p>
                当前版本为内容浏览演示平台，站内影视资料、评分与图片均为示例内容，用于展示平台的浏览与检索体验，暂不提供真实在线播放服务。
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">平台数据</h3>
            <div className="flex flex-col gap-4">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-heading font-bold text-primary-600">30+</span>
                <span className="text-sm font-body text-foreground-500">部精选影视作品</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-heading font-bold text-accent-500">6</span>
                <span className="text-sm font-body text-foreground-500">大地区内容覆盖</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-heading font-bold text-secondary-600">10+</span>
                <span className="text-sm font-body text-foreground-500">种题材分类</span>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">我们的坚持</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v) => (
              <div key={v.title} className="p-5 rounded-lg border border-background-200 bg-background-100/60">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 mb-4">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="text-sm font-heading font-semibold text-foreground-950">{v.title}</h3>
                <p className="mt-1.5 text-xs font-body leading-relaxed text-foreground-500">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="contact" className="scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">联系我们</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-1 flex flex-col gap-4">
              <div className="flex items-start gap-3 p-5 rounded-lg border border-background-200 bg-background-50">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-950">邮箱</h3>
                  <p className="mt-1 text-xs font-body text-foreground-500 break-all">support@tianlong-cinema.demo</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-5 rounded-lg border border-background-200 bg-background-50">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-accent-50 text-accent-600">
                  <i className="ri-customer-service-2-line text-lg"></i>
                </span>
                <div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-950">反馈</h3>
                  <p className="mt-1 text-xs font-body text-foreground-500 leading-relaxed">
                    版权合作、内容纠错或使用建议，欢迎通过右侧表单留言。
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
              <h3 className="text-base font-heading font-bold text-foreground-950 mb-1">在线反馈</h3>
              <p className="text-xs font-body text-foreground-500 mb-5">填写以下信息，我们会认真阅读每一条反馈。</p>

              <form
                id="tianlong-feedback-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="fb-name" className="text-xs font-label text-foreground-600">
                      您的称呼
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入称呼"
                      className="h-11 px-4 rounded-md border border-background-300 bg-background-100 text-sm font-label text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="fb-email" className="text-xs font-label text-foreground-600">
                      邮箱
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="h-11 px-4 rounded-md border border-background-300 bg-background-100 text-sm font-label text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="fb-topic" className="text-xs font-label text-foreground-600">
                    反馈类型
                  </label>
                  <select
                    id="fb-topic"
                    name="topic"
                    defaultValue="使用建议"
                    className="h-11 px-4 rounded-md border border-background-300 bg-background-100 text-sm font-label text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                  >
                    <option value="使用建议">使用建议</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="版权合作">版权合作</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="fb-message" className="text-xs font-label text-foreground-600">
                    留言内容
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请描述您的反馈内容（不超过 500 字）"
                    className="px-4 py-3 rounded-md border border-background-300 bg-background-100 text-sm font-body text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                  ></textarea>
                </div>

                <input
                  className="form-field-alt"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} text-base`}></i>
                    </span>
                    {status === "loading" ? "提交中..." : "提交反馈"}
                  </button>
                  {status === "success" && (
                    <span className="flex items-center gap-1.5 text-sm font-body text-primary-600">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-checkbox-circle-line text-base"></i>
                      </span>
                      提交成功，感谢您的反馈！
                    </span>
                  )}
                  {status === "error" && (
                    <span className="flex items-center gap-1.5 text-sm font-body text-accent-700">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-error-warning-line text-base"></i>
                      </span>
                      {formError || "提交失败，请稍后重试"}
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>

        <section id="copyright" className="scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">版权信息与使用说明</h2>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7 flex flex-col gap-4 text-sm font-body leading-loose text-foreground-700">
            <p>
              © 2026 天龙影院。本站为内容浏览演示平台，站内所有影视名称、简介、评分、演职人员及图片均为示例内容，仅用于展示界面的浏览与检索功能，不代表任何真实影视作品或商业资源。
            </p>
            <p>
              本站不存储、不提供任何影视作品的真实在线播放服务。如需观看相关内容，请通过正规渠道获取授权资源。若站内示例内容无意中涉及您的权益，请通过上方反馈表单与我们联系。
            </p>
            <p>
              用户在使用本站过程中产生的本地收藏数据仅保存在个人浏览器中，不会上传至服务器，亦不会用于其他用途。
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to="/category/movie"
                className="px-4 py-2 rounded-md bg-background-100 text-sm font-label text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
              >
                浏览电影
              </Link>
              <Link
                to="/category/tv"
                className="px-4 py-2 rounded-md bg-background-100 text-sm font-label text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
              >
                浏览电视剧
              </Link>
              <Link
                to="/search"
                className="px-4 py-2 rounded-md bg-background-100 text-sm font-label text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
              >
                搜索影视
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}